import { Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface FixInventoryResult {
  success: boolean;
  equipmentChecked: number;
  equipmentUpdated: number;
  errors: number;
  error?: string;
}

export default function AdminMaintenancePage() {
  const queryClient = useQueryClient();

  const fixInventory = useMutation({
    mutationFn: async (): Promise<FixInventoryResult> => {
      const res = await fetch("/api/maintenance/fix-inventory-count", {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fix inventory");
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["equipment"] });
      toast.success(
        `Checked ${data.equipmentChecked} items, updated ${data.equipmentUpdated}` +
          (data.errors > 0 ? ` (${data.errors} errors)` : ""),
      );
    },
    onError: (err: Error) => {
      toast.error(`Fix failed: ${err.message}`);
    },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Maintenance" />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base">Fix Inventory Count</CardTitle>
            </div>
            <CardDescription>
              Fetches current stock quantities from Rentman and updates local
              equipment records. Use this to fix inventory numbers that were not
              synced correctly during the initial import.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              onClick={() => fixInventory.mutate()}
              disabled={fixInventory.isPending}
            >
              {fixInventory.isPending ? "Fixing..." : "Fix Inventory Count"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
